
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Category } from './useCategories';
import { useTranslation } from 'react-i18next';
import { useParameters } from './useParameters';

export type CategoryGroup = {
  id: string;
  description: string | null;
  created_at: string;
  translation_key: string;
  name?: string; // For backward compatibility
  categories?: Category[];
  isDefault?: boolean;
};

export const useCategoryGroups = () => {
  const { i18n } = useTranslation();
  const currentLocale = i18n.language;
  const { data: parametersData } = useParameters();

  return useQuery({
    queryKey: ['categoryGroups', currentLocale, parametersData],
    queryFn: async () => {
      // Fetch category groups from Supabase along with their default translations
      const { data: categoryGroups, error: groupsError } = await supabase
        .from('category_groups')
        .select(`
          id, 
          description, 
          created_at, 
          translation_key,
          translation_defaults!inner(key, default_value)
        `)
        .order('translation_key');

      if (groupsError) {
        console.error('Error fetching category groups:', groupsError);
        throw new Error(groupsError.message);
      }

      // Fetch community-specific translations if they exist
      const { data: communityTranslations, error: communityTranslationsError } = await supabase
        .from('community_translations')
        .select('translation_key, value')
        .eq('locale', currentLocale)
        .in('translation_key', categoryGroups.map(g => g.translation_key));

      if (communityTranslationsError) {
        console.error('Error fetching community translations:', communityTranslationsError);
      }

      // Create a map of translation overrides
      const translationOverrides = new Map();
      if (communityTranslations) {
        communityTranslations.forEach(t => {
          translationOverrides.set(t.translation_key, t.value);
        });
      }

      // Find the default category group from parameters - fixed to use the new structure
      const defaultGroupKey = parametersData?.parameters?.['default_category_group']?.value || '';
      console.log('Default category group key from parameters:', defaultGroupKey);

      // Process the category groups with their translations
      const processedGroups = categoryGroups.map(group => {
        // Get the default value from translation_defaults
        const defaultName = group.translation_defaults.default_value;
        
        // Check if there's a community-specific translation override
        const translatedName = translationOverrides.get(group.translation_key) || defaultName;
        
        return {
          id: group.id,
          description: group.description,
          created_at: group.created_at,
          translation_key: group.translation_key,
          name: translatedName,
          isDefault: group.translation_key === defaultGroupKey,
        };
      });

      // Fetch all categories to organize them by group
      const { data: categories, error: categoriesError } = await supabase
        .from('categories')
        .select(`
          id, 
          description, 
          created_at, 
          category_group_id, 
          translation_key,
          translation_defaults!inner(key, default_value)
        `)
        .order('translation_key');

      if (categoriesError) {
        console.error('Error fetching categories:', categoriesError);
        throw new Error(categoriesError.message);
      }

      // Fetch community-specific translations for categories if they exist
      const { data: categoryTranslations, error: categoryTranslationsError } = await supabase
        .from('community_translations')
        .select('translation_key, value')
        .eq('locale', currentLocale)
        .in('translation_key', categories.map(c => c.translation_key));

      if (categoryTranslationsError) {
        console.error('Error fetching category translations:', categoryTranslationsError);
      }

      // Create a map of category translation overrides
      const categoryTranslationOverrides = new Map();
      if (categoryTranslations) {
        categoryTranslations.forEach(t => {
          categoryTranslationOverrides.set(t.translation_key, t.value);
        });
      }

      // Process the categories with their translations
      const processedCategories = categories.map(category => {
        // Get the default value from translation_defaults
        const defaultName = category.translation_defaults.default_value;
        
        // Check if there's a community-specific translation override
        const translatedName = categoryTranslationOverrides.get(category.translation_key) || defaultName;
        
        return {
          id: category.id,
          description: category.description,
          created_at: category.created_at,
          category_group_id: category.category_group_id,
          translation_key: category.translation_key,
          name: translatedName, // Add the translated name for backward compatibility
        };
      });

      // Organize categories by their group
      const groupsWithCategories = processedGroups.map((group: any) => {
        const groupCategories = processedCategories.filter(
          (category: Category) => 
            category.category_group_id === group.id
        );
        
        return {
          ...group,
          categories: groupCategories
        };
      });

      return {
        categoryGroups: groupsWithCategories,
        // Flat list of all category groups (for UI display)
        categoryGroupsList: processedGroups.map((group: any) => ({
          id: group.id,
          name: group.name,
          description: group.description,
          translation_key: group.translation_key,
          isDefault: group.isDefault
        }))
      };
    },
    enabled: !!parametersData, // Only run after parameters are loaded
  });
};
